import { useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Crown, Building, Zap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

const PLAN_CARDS = [
  {
    id: "free",
    name: "Free",
    price: 0,
    description: "Core features to get started",
    icon: Zap,
    color: "text-gray-600",
    badge: null,
    features: [
      "Up to 10 goals",
      "Up to 20 tasks",
      "Basic habit tracking",
      "Journal & notes",
      "Community access",
    ],
    cta: "Continue with Free",
    variant: "outline" as const,
  },
  {
    id: "pro",
    name: "Pro",
    price: null,
    description: "For serious goal achievers",
    icon: Crown,
    color: "text-primary",
    badge: "Most Popular",
    features: [
      "Unlimited goals & tasks",
      "AI coaching & insights",
      "Google Calendar sync",
      "Time budget planner",
      "Advanced analytics",
    ],
    cta: "Choose Pro",
    variant: "default" as const,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: null,
    description: "Everything, unlimited",
    icon: Building,
    color: "text-purple-600",
    badge: null,
    features: [
      "Everything in Pro",
      "Custom branding",
      "API access",
      "Priority support",
      "Team collaboration",
    ],
    cta: "Choose Enterprise",
    variant: "outline" as const,
  },
];

export default function TrialExpiredPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selecting, setSelecting] = useState<string | null>(null);

  const { data: plansData } = useQuery<any[]>({
    queryKey: ["/api/subscription-plans"],
  });

  const downgradeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/billing/select-free-plan", {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({
        title: "Free plan activated",
        description: "You're now on the free plan. Upgrade any time from settings.",
      });
      setLocation("/");
    },
    onError: () => {
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    },
  });

  const handlePlanSelect = (planId: string) => {
    setSelecting(planId);
    if (planId === "free") {
      downgradeMutation.mutate();
    } else {
      setLocation("/pricing");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-900 dark:to-gray-800 flex flex-col items-center justify-center p-4">
      <div className="w-full space-y-8">

        {/* Header */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-4 py-2 rounded-full text-sm font-semibold">
            Your free trial has ended
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
            Choose the plan that's right for you
          </h1>
          <p className="text-gray-600 dark:text-gray-300 max-w-xl mx-auto text-lg">
            You've experienced the best of GoalVia. Now pick a plan to keep going — or continue with the free tier at no cost.
          </p>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLAN_CARDS.map((plan) => {
            const Icon = plan.icon;
            const isLoading = selecting === plan.id && (downgradeMutation.isPending || plan.id !== "free");

            return (
              <Card
                key={plan.id}
                className={`relative flex flex-col ${plan.id === "pro" ? "border-primary shadow-xl scale-105" : "shadow-md"}`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-white px-3 py-1 text-xs">
                      {plan.badge}
                    </Badge>
                  </div>
                )}
                <CardHeader className="pb-4">
                  <div className={`${plan.color} mb-2`}>
                    <Icon className="h-7 w-7" />
                  </div>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  {plan.price === 0 && (
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">Free</p>
                  )}
                  {plan.price === null && (
                    <p className="text-sm text-gray-500 mt-1">See pricing for details</p>
                  )}
                </CardHeader>
                <CardContent className="flex-1">
                  <ul className="space-y-2">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full"
                    variant={plan.variant}
                    onClick={() => handlePlanSelect(plan.id)}
                    disabled={downgradeMutation.isPending || !!selecting}
                  >
                    {isLoading ? "Please wait…" : plan.cta}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        <p className="text-center text-sm text-gray-500">
          You can upgrade, downgrade, or cancel any time from your account settings.
          Paid plans require a credit card via our secure Stripe checkout.
        </p>
      </div>
    </div>
  );
}
